import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SubAccountManagement } from '../SubAccountManagement';
import Wallet from './Wallet';
import Password from '../ChangePassword/Password';
import TOTPSetup from '../auth/TOTPSetup';
import ApiKeys from './ApiKeys';
import { faUser, faShieldHalved, faWallet, faLock, faArrowLeft, faChevronRight, faKey } from '@fortawesome/free-solid-svg-icons';
import { useAccount } from '../../context/AccountContext';

interface SettingsLayoutProps {
    activeTabExit: () => void;
    defaultTab?: SettingsTab;
}

type SettingsTab = 'accounts' | 'payout' | 'security-2fa' | 'security-password' | 'api-keys';

function SettingsLayout({ activeTabExit, defaultTab }: SettingsLayoutProps) {
    const [activeTab, setActiveTab] = useState<SettingsTab>(defaultTab || 'accounts');
    const { activeAccount } = useAccount();
    const [showMobileContent, setShowMobileContent] = useState(false);

    const handleTabChange = (tab: SettingsTab) => {
        setActiveTab(tab);
        setShowMobileContent(true);
    };

    const navItems = [
        { id: 'accounts', label: 'Manage Accounts', icon: faUser, description: 'Switch or create sub-accounts' },
        { id: 'payout', label: 'Payout Settings', icon: faWallet, description: 'Manage wallet addresses' },
        { id: 'security-2fa', label: '2FA Security', icon: faShieldHalved, description: 'Two-factor authentication' },
        { id: 'security-password', label: 'Change Password', icon: faLock, description: 'Update security credentials' },
        { id: 'api-keys', label: 'API Keys', icon: faKey, description: 'Manage your API access keys' },
    ];

    const renderContent = () => {
        switch (activeTab) {
            case 'accounts':
                return (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <SubAccountManagement />
                    </div>
                );
            case 'payout':
                return (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <Wallet />
                    </div>
                );
            case 'security-2fa':
                return (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <TOTPSetup activeTabExit={() => { }} />
                    </div>
                );
            case 'security-password':
                return (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <Password activeTabExit={() => { }} />
                    </div>
                );
            case 'api-keys':
                return (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <ApiKeys />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col md:flex-row min-h-[600px] gap-6 p-4 md:p-0">
            {/* Sidebar / Mobile Menu */}
            <div className={`w-full md:w-64 flex flex-col gap-2 ${showMobileContent ? 'hidden md:flex' : 'flex'}`}>
                <div className="mb-4">
                    <button
                        onClick={activeTabExit}
                        className="flex items-center gap-2 px-4 py-2 text-slate-500 dark:text-slate-400 hover:text-[#0A3A6B] dark:hover:text-blue-400 transition-colors group mb-2"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="text-sm transition-transform group-hover:-translate-x-1" />
                        <span className="text-xs font-bold uppercase tracking-widest">Back to Home</span>
                    </button>
                    <div className="hidden md:block">
                        <h2 className="text-xl font-bold text-slate-800 dark:text-white px-4">Account Settings</h2>
                        <p className="text-xs text-slate-500 dark:text-slate-400 px-4 mt-1">Manage your security and preferences</p>
                    </div>
                </div>

                <div className="flex flex-col gap-3">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => handleTabChange(item.id as SettingsTab)}
                            className={`w-full flex items-center justify-between p-4 rounded-[1.5rem] transition-all duration-300 group
                                ${activeTab === item.id
                                    ? 'bg-[#0A3A6B] text-white shadow-lg shadow-blue-500/20'
                                    : 'bg-white/50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 border border-transparent hover:border-slate-200 dark:hover:border-slate-700'
                                }`}
                        >
                            <div className="flex items-center gap-4 text-left">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors
                                    ${activeTab === item.id ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-700 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30'}
                                `}>
                                    <FontAwesomeIcon icon={item.icon} className={activeTab === item.id ? 'text-white' : 'text-slate-500 dark:text-slate-400 group-hover:text-blue-500'} />
                                </div>
                                <div>
                                    <p className="font-bold text-sm tracking-tight">{item.label}</p>
                                    <p className={`text-[10px] ${activeTab === item.id ? 'text-white/60' : 'text-slate-400'} hidden md:block`}>{item.description}</p>
                                </div>
                            </div>
                            <FontAwesomeIcon icon={faChevronRight} className={`text-[10px] transition-transform ${activeTab === item.id ? 'text-white opacity-40 translate-x-1' : 'text-slate-300 opacity-0 group-hover:opacity-100'}`} />
                        </button>
                    ))}
                </div>
            </div>

            {/* Content Area */}
            <div className={`flex-1 min-h-[600px] relative ${!showMobileContent ? 'hidden md:block' : 'block'}`}>
                {/* Mobile Header with Back Button */}
                <div className="md:hidden flex items-center gap-4 mb-6 sticky top-0 bg-gray-50 dark:bg-slate-800 py-2 z-20">
                    <button
                        onClick={() => setShowMobileContent(false)}
                        className="w-10 ml-4 h-10 bg-white dark:bg-slate-700 rounded-full shadow-md flex items-center justify-center text-slate-600 dark:text-slate-300"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <h2 className="text-lg md:text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
                        {navItems.find(i => i.id === activeTab)?.label}
                    </h2>
                </div>

                <div className="md:bg-white/60 md:dark:bg-slate-800/60 md:backdrop-blur-xl md:border md:border-white/40 md:dark:border-slate-700/50 md:rounded-[2.5rem] p-1 md:p-10 md:shadow-xl md:overflow-hidden md:min-h-full">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
}

export default SettingsLayout;
